import { SfoUiJSONSchema7 } from '../../metadata.model';

export class FieldComparisonHelper {
  /**
   * Compares the current form fields with a target schema to identify fields that would be lost
   * @param currentFormValue The current form values
   * @param targetSchema The schema we're switching to
   * @returns Object containing fields that would be lost and their current values
   */
  static getFieldsToLose(
    currentFormValue: any,
    targetSchema: SfoUiJSONSchema7,
  ): { lostFields: string[]; fieldValues: { [key: string]: any } } {
    if (!currentFormValue) {
      return { lostFields: [], fieldValues: {} };
    }

    const lostFields: string[] = [];
    const fieldValues: { [key: string]: any } = {};

    const currentRootFields = Object.keys(currentFormValue);
    const targetRootFields = Object.keys(targetSchema?.properties || {});

    currentRootFields.forEach((fieldName) => {
      if (
        !targetRootFields.includes(fieldName) &&
        this.hasNonEmptyValue(currentFormValue, fieldName)
      ) {
        lostFields.push(fieldName);
        fieldValues[fieldName] = currentFormValue[fieldName];
      }
    });

    return { lostFields, fieldValues };
  }

  /**
   * Checks if a field path has a non-empty value
   * Performs deep checking to detect truly empty nested structures
   */
  private static hasNonEmptyValue(obj: any, fieldName: string): boolean {
    const value = obj[fieldName];
    return this.isValueNonEmpty(value);
  }

  /**
   * Recursively checks if a value is truly non-empty
   */
  private static isValueNonEmpty(value: any): boolean {
    // Primitive empty checks
    if (value === null || value === undefined || value === '') {
      return false;
    }

    // Array checks
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return false;
      }
      // Check if all array items are empty
      return value.some((item) => this.isValueNonEmpty(item));
    }

    // Object checks
    if (typeof value === 'object') {
      const keys = Object.keys(value);
      if (keys.length === 0) {
        return false;
      }
      // Check if all object values are empty
      return keys.some((key) => this.isValueNonEmpty(value[key]));
    }

    // Non-empty primitive (number, boolean, string with content)
    return true;
  }
}
